import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { Notice } from '../../components/primitives';
import { request, type ApiError } from '../../api/client';
import './policies.css';

export function GenericPolicyEditorScreen({ scope }: { scope: 'organization' | 'cleric' }) {
  const { t } = useTranslation('common');
  const params = useParams({ strict: false });
  const ownerId = scope === 'organization' ? params.id : 'me';
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['generic-policy', scope, ownerId],
    queryFn: () =>
      request<{ rules: unknown[]; policy_revision: number; policy_name: string }>(
        scope === 'organization'
          ? `/organizations/${ownerId}/selection-policy`
          : '/me/selection-policy',
      ),
  });

  const mutation = useMutation({
    mutationFn: (newRules: unknown[]) =>
      request(
        scope === 'organization'
          ? `/organizations/${ownerId}/selection-policy`
          : '/me/selection-policy',
        {
          method: 'PUT',
          body: JSON.stringify({ rules: newRules, expected_revision: query.data?.policy_revision }),
        },
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['generic-policy', scope, ownerId] }),
  });

  const [draft, setDraft] = useState<string | null>(null);

  if (query.isPending) return <p className="pol-state">{t('policies.library.loading')}</p>;
  if (query.isError)
    return (
      <Notice referenceId={(query.error as ApiError).correlationId}>
        {(query.error as ApiError).message}
      </Notice>
    );

  const currentRulesStr = JSON.stringify(query.data.rules, null, 2);
  const displayDraft = draft ?? currentRulesStr;

  const handleSave = () => {
    try {
      const parsed = JSON.parse(displayDraft);
      mutation.mutate(parsed);
      setDraft(null);
    } catch {
      alert('Invalid JSON');
    }
  };

  return (
    <div className="pol-lib">
      <header className="pol-lib__head">
        <div>
          <p className="pol__eyebrow">
            {scope === 'organization'
              ? t('policies.library.scope_org_title')
              : t('policies.library.scope_cleric_title')}
          </p>
          <h1 className="pol-lib__title">{t('policies.generic.raw_editor')}</h1>
        </div>
        <Link to="/policies" className="pol__link">
          {t('policies.generic.back_library')}
        </Link>
      </header>

      {mutation.isError && (
        <Notice referenceId={(mutation.error as ApiError).correlationId}>
          {(mutation.error as ApiError).message}
        </Notice>
      )}

      <div style={{ marginTop: 24 }}>
        <p className="pol__lede">{t('policies.generic.note')}</p>
        <textarea
          style={{
            width: '100%',
            height: 400,
            fontFamily: 'monospace',
            padding: 12,
            border: '1px solid var(--ink-border)',
          }}
          value={displayDraft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div style={{ marginTop: 16 }}>
          <button
            className="pol__btn"
            onClick={handleSave}
            disabled={mutation.isPending || displayDraft === currentRulesStr}
          >
            {mutation.isPending ? t('policies.generic.saving') : t('policies.generic.save_rules')}
          </button>
        </div>
      </div>
    </div>
  );
}
